import { FileType2, Info } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { usePersonelList } from '@/features/hr/personel/hooks/usePersonelStore'
import { useDocumentVariables } from '../api'
import { cn } from '@/lib/utils'
import { sampleValuesForPersonel } from '../lib/sampleValues'
import type { SablonSurum } from '../types'
import { DocumentCanvas } from './DocumentCanvas'

type Props = {
  surum: SablonSurum
  className?: string
  defaultFillMode?: 'empty' | 'sample'
  defaultZoom?: number
}

export function TemplatePreview({
  surum,
  className,
  defaultFillMode = 'sample',
  defaultZoom = 0.85,
}: Props) {
  const { t } = useTranslation()
  const people = usePersonelList()
  const variables = useDocumentVariables().data ?? []
  const [personelId, setPersonelId] = useState(people[0]?.id ?? '')
  const [fillMode, setFillMode] = useState<'empty' | 'sample'>(defaultFillMode)
  const [zoom, setZoom] = useState(defaultZoom)
  const [pageId, setPageId] = useState(surum.pages[0]?.id ?? '')
  const sampleValues = useMemo(() => sampleValuesForPersonel(people.find((person) => person.id === personelId), variables), [people, personelId, variables])
  const activePageId = surum.pages.some((p) => p.id === pageId) ? pageId : surum.pages[0]?.id ?? ''

  if (surum.method === 'pdf') {
    const url = surum.uploadedFile?.previewUrl
    return (
      <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
        <div className="flex flex-wrap items-center gap-2 border-b border-border/70 px-3 py-2">
          <span className="truncate text-sm font-medium">{surum.uploadedFile?.name ?? t('hr.belgeler.method.pdf')}</span>
          <Badge tone="neutral">PDF</Badge>
        </div>
        {url ? (
          <iframe
            title={surum.uploadedFile?.name ?? 'pdf-preview'}
            src={url}
            className="min-h-[28rem] w-full flex-1 border-0 bg-muted/30"
          />
        ) : (
          <div className="flex min-h-[20rem] flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
            <Info className="size-5 text-info" />
            <p className="font-medium text-foreground">{t('hr.belgeler.preview.pdfReloadTitle')}</p>
            <p className="max-w-md text-xs">{t('hr.belgeler.preview.pdfReloadBody')}</p>
          </div>
        )}
      </div>
    )
  }

  const isDocx = surum.method === 'docx'
  const mapping = surum.variableMappings ?? []
  const counts = {
    eslesti: mapping.filter((m) => m.status === 'eslesti').length,
    manuel: mapping.filter((m) => m.status === 'manuel').length,
    tanimsiz: mapping.filter((m) => m.status === 'tanimsiz').length,
  }

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
      <div className="flex flex-wrap items-center gap-2 border-b border-border/70 px-3 py-2">
        {isDocx ? (
          <Badge tone="warning" className="gap-1">
            <FileType2 className="size-3" />
            {t('hr.belgeler.preview.demoBadge')}
          </Badge>
        ) : null}
        <Label htmlFor="preview-sample" className="text-xs">
          {t('hr.belgeler.preview.sampleEmployee')}
        </Label>
        <Select
          id="preview-sample"
          size="sm"
          className="w-auto max-w-[12rem]"
          value={personelId}
          onChange={setPersonelId}
          options={people.map((p) => ({ value: p.id, label: p.ad }))}
        />
        <div className="flex rounded-md border border-border p-0.5 text-[11px]">
          <button
            type="button"
            className={cn('rounded px-2 py-1', fillMode === 'empty' && 'bg-muted')}
            onClick={() => setFillMode('empty')}
          >
            {t('hr.belgeler.preview.empty')}
          </button>
          <button
            type="button"
            className={cn('rounded px-2 py-1', fillMode === 'sample' && 'bg-muted')}
            onClick={() => setFillMode('sample')}
          >
            {t('hr.belgeler.preview.filled')}
          </button>
        </div>
        {surum.pages.length > 1 ? (
          <Select
            size="sm"
            className="w-auto max-w-[12rem]"
            value={activePageId}
            onChange={setPageId}
            options={surum.pages.map((p) => ({ value: p.id, label: p.title }))}
            aria-label={t('hr.belgeler.structure.pages')}
          />
        ) : null}
      </div>

      {isDocx ? (
        <div className="flex flex-wrap items-start gap-2 border-b border-border/60 bg-warning/8 px-3 py-2 text-[11px] text-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0 text-warning" />
          <div className="min-w-0 flex-1">
            <p>{t('hr.belgeler.preview.docxNote')}</p>
            {mapping.length ? (
              <p className="mt-1 text-muted-foreground">
                {t('hr.belgeler.preview.mappingSummary', counts)}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <DocumentCanvas
        surum={surum}
        pageId={activePageId}
        zoom={zoom}
        onZoomChange={setZoom}
        editable={false}
        showToolbar={false}
        fillMode={fillMode}
        sampleValues={sampleValues}
      />
    </div>
  )
}
